<template>
  <component
    :is="tag"
    :class="[
      'btn',
      `btn-${variant}`,
      size !== 'md' && `btn-${size}`,
      iconOnly && 'btn-icon-only',
      loading && 'is-loading',
    ]"
    :type="tag === 'button' ? type : undefined"
    :disabled="tag === 'button' ? (disabled || loading) : null"
    :aria-busy="loading || null"
    @click="onClick"
  >
    <Icon v-if="loading" name="Loader2" :size="iconSize" class="btn__spinner" />
    <Icon v-else-if="icon && !iconRight" :name="icon" :size="iconSize" />
    <span v-if="!iconOnly" class="btn__label"><slot /></span>
    <Icon v-if="icon && iconRight && !loading" :name="icon" :size="iconSize" />
  </component>
</template>

<script>
import Icon from './Icon.vue';

/**
 * BaseButton — unified flat button that respects the global design system.
 *
 * Props:
 *   variant : primary | secondary | success | warning | danger | info | outline | ghost | default
 *   size    : sm | md | lg
 *   icon    : Lucide icon name (optional)
 *   iconRight: render the icon to the right of the label
 *   iconOnly: render only the icon, with square footprint
 *   loading : show spinner and disable
 *   tag     : html element — useful for <router-link>-style usage ('a', 'button', etc.)
 */
export default {
  name: 'BaseButton',
  components: { Icon },
  props: {
    variant: { type: String, default: 'default' },
    size: { type: String, default: 'md' },
    icon: { type: String, default: '' },
    iconRight: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    type: { type: String, default: 'button' },
    tag: { type: String, default: 'button' },
  },
  emits: ['click'],
  computed: {
    iconSize() {
      if (this.size === 'sm') return 14;
      if (this.size === 'lg') return 20;
      return 16;
    },
  },
  methods: {
    onClick(e) {
      if (this.disabled || this.loading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.$emit('click', e);
    },
  },
};
</script>

<style scoped>
.btn__label { white-space: nowrap; }

.is-loading {
  pointer-events: none;
  opacity: 0.9;
}

.btn__spinner {
  animation: btn-spin 1s linear infinite;
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}
</style>
